import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AtSign, Send, UserCheck } from 'lucide-react';
import { TEAMS_MESSAGE_EXAMPLE_CONTEXT, mentionToken, parseTeamsMessage } from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { Alert, Badge, Button, Input, Label, Textarea } from '@/components/ui';
import type { NotificationRecipient } from '@/types';

interface TestResult {
  delivered: boolean;
  configured: boolean;
  mentions: string[];
  unknown_mentions: string[];
  status?: number;
  error?: string;
}

/**
 * Redigerer beskeden en regel sender til Teams-kanalen.
 *
 * **Ét tekstfelt.** Omtaler skrives inde i teksten frem for i et felt ved siden af, fordi det
 * er dér, de står i den færdige besked. Syntaksen ligger i `parseTeamsMessage` i
 * @codepass/shared, og forhåndsvisningen nedenfor bruger præcis den samme funktion som
 * afsendelsen — en visning der viste noget andet end kortet, ville være værre end ingen.
 *
 * "Nævn en bruger" indsætter markeringen ved markøren, så syntaksen kan bruges uden at læres
 * udenad. Den kan stadig skrives i hånden; feltet er almindelig tekst.
 *
 * "Send test til Teams" sender kladden til kanalen. Forhåndsvisningen kan vise formen, men
 * ikke om webhooken virker, og heller ikke om omtalen faktisk prikker nogen — det afhænger af,
 * hvilken slags connector adressen peger på, og kan kun ses i Teams.
 */
export function TeamsMessageEditor({
  id,
  value,
  onChange,
  /** Kortets overskrift, når der sendes en test. Reglens titel. */
  title,
}: {
  id: string;
  value: string;
  onChange: (message: string) => void;
  title: string;
}) {
  const field = useRef<HTMLTextAreaElement | null>(null);
  const canTest = useAuth().can('rules.manage');
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['notification-recipients'],
    queryFn: () => api.get<{ data: NotificationRecipient[] }>('/notifications/recipients'),
    /** Brugerlisten skifter sjældent, og vælgeren åbnes ofte. */
    staleTime: 5 * 60 * 1000,
  });

  const people = useMemo(() => data?.data ?? [], [data]);

  /**
   * Forhåndsvisningen. `{{…}}`-pladserne får eksempelværdier, så beskeden kan ses som en rigtig
   * besked frem for som en skabelon.
   *
   * `{{modtager}}` får en rigtig bruger fra listen, fordi den bliver til en **omtale** og ikke
   * til tekst — vises den som et eksempelnavn, kan man ikke se, at den prikker nogen. Hvem det
   * er i visningen betyder ikke noget: reglen sætter den, alarmen handler om.
   */
  const preview = useMemo(() => {
    const sample = people.find((person) => person.active) ?? people[0];
    const context = {
      ...TEAMS_MESSAGE_EXAMPLE_CONTEXT,
      ...(sample ? { modtager: mentionToken(sample.email) } : {}),
    };
    return parseTeamsMessage(value, people, context);
  }, [value, people]);

  /**
   * Sender kortet til Teams-kanalen, som det ser ud lige nu.
   *
   * **Kladden og ikke den gemte række.** Pointen er at kunne prøve en ændring, før den gemmes;
   * læste serveren reglen i databasen, ville testen vise den forrige version.
   */
  const test = useMutation({
    mutationFn: () =>
      api.post<TestResult>('/notifications/test-rule-message', {
        title,
        teams_message: value,
      }),
  });

  /** Indsætter ved markøren og flytter den bagud, så der kan skrives videre. */
  const insert = (token: string): void => {
    const input = field.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;

    onChange(next);

    // Efter re-renderen: uden ventetiden sætter React markøren tilbage til slutningen.
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const term = search.trim().toLowerCase();
  const matches = people.filter(
    (person) =>
      term.length === 0 ||
      person.name.toLowerCase().includes(term) ||
      person.email.toLowerCase().includes(term),
  );

  return (
    <div className="space-y-2">
      <Textarea
        id={id}
        ref={field}
        rows={6}
        className="font-mono text-xs"
        placeholder={
          'Fx: @{bkha@onecode.dk} — opgaven {{opgave}} på {{kontrakt}} har brugt flere timer ' +
          'end estimeret.'
        }
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />

      <div className="flex flex-wrap items-center gap-2">
        {/*
          Den dynamiske omtale. Skrives en fast adresse ind i stedet, nævner reglen den samme
          person hver gang — også når alarmen handler om en anden. `{{modtager}}` bliver til
          den, opgaven tilhører, når alarmen rejses.
        */}
        <Button size="sm" variant="secondary" onClick={() => insert('{{modtager}}')}>
          <UserCheck className="mr-1 h-3.5 w-3.5" />
          Nævn den ansvarlige
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setPicking((open) => !open)}>
          <AtSign className="mr-1 h-3.5 w-3.5" />
          Nævn en bestemt bruger
        </Button>
        {/*
          Sender kladden, ikke den gemte regel — se `test`. Slået fra på en tom besked: der er
          ingenting at sende, og serveren afviser den alligevel.
        */}
        {/* The test is 'Ændr regler' - Auditoprydning can be edited without it. */}
        {canTest ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={preview.lines.length === 0}
            loading={test.isPending}
            onClick={() => test.mutate()}
          >
            <Send className="mr-1 h-3.5 w-3.5" />
            Send test til Teams
          </Button>
        ) : null}
        <span className="text-xs text-ink-muted">
          <code className="font-mono">{'{{modtager}}'}</code> nævner den, alarmen handler om —
          opgavens ejer, eller aftalens ansvarlige. Pladserne{' '}
          <code className="font-mono">{'{{opgave}}'}</code>,{' '}
          <code className="font-mono">{'{{kontrakt}}'}</code> og{' '}
          <code className="font-mono">{'{{milepael}}'}</code> udfyldes af reglen, når den finder en
          årsag.
        </span>
      </div>

      <TestOutcome
        result={test.data}
        error={test.error}
        pending={test.isPending}
        settled={test.isSuccess || test.isError}
      />

      {picking ? (
        <div className="space-y-2 rounded-md border border-line p-2">
          <Label htmlFor={`${id}-search`}>Brugere fra /admin/brugere</Label>
          <Input
            id={`${id}-search`}
            className="h-8"
            placeholder="Søg navn eller email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          {isLoading ? (
            <p className="text-xs text-ink-muted">Henter brugere …</p>
          ) : matches.length === 0 ? (
            <p className="text-xs text-ink-muted">Ingen brugere matcher.</p>
          ) : (
            <ul className="max-h-48 space-y-1 overflow-y-auto">
              {matches.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    onClick={() => insert(mentionToken(person.email))}
                    className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-canvas focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
                  >
                    <span className="font-medium">{person.name}</span>
                    <span className="text-ink-muted">{person.email}</span>
                    <span className="ml-auto flex items-center gap-1">
                      {person.role ? <Badge tone="neutral">{person.role_label}</Badge> : null}
                      {/*
                        Deaktiverede står i listen, men mærket: en regel kan være skrevet
                        dengang de var aktive, og en omtale af en der er holdt op, skal kunne
                        opdages frem for bare at være væk.
                      */}
                      {person.active ? null : <Badge tone="warning">Inaktiv</Badge>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {preview.unknownMentions.length > 0 ? (
        <Alert tone="warning">
          Ingen bruger med {preview.unknownMentions.length === 1 ? 'adressen' : 'adresserne'}{' '}
          {preview.unknownMentions.join(', ')}. Kortet sendes, men{' '}
          {preview.unknownMentions.length === 1 ? 'adressen står' : 'adresserne står'} som ren tekst
          og nævner ingen.
        </Alert>
      ) : null}

      <TeamsMessagePreview preview={preview} />
    </div>
  );
}

/**
 * Hvad der kom ud af testen.
 *
 * Tre udfald, og de skal skelnes: leveret, ikke opsat, og afvist. **Ikke opsat er ikke en
 * fejl** — `TEAMS_WEBHOOK` er bare tom, og det rettes et andet sted end her, så beskeden peger
 * derhen frem for at ligne noget galt med reglen.
 *
 * Adressen står aldrig i en besked herfra. Den bærer sin egen adgang; enhver med URL'en kan
 * skrive i kanalen — se noten i teams-webhook.ts.
 */
function TestOutcome({
  result,
  error,
  pending,
  settled,
}: {
  result: TestResult | undefined;
  error: unknown;
  pending: boolean;
  settled: boolean;
}) {
  if (pending || !settled) return null;

  if (error) {
    return (
      <Alert tone="error">
        {error instanceof ApiError ? error.message : 'Testkortet kunne ikke sendes.'}
      </Alert>
    );
  }

  if (!result) return null;

  if (!result.configured) {
    return (
      <Alert tone="info">
        Der er ingen Teams-webhook sat op. Sæt <code className="font-mono">TEAMS_WEBHOOK</code>{' '}
        under /admin/integrationer, så kan testen sendes.
      </Alert>
    );
  }

  if (!result.delivered) {
    return <Alert tone="error">{result.error ?? 'Teams tog ikke imod kortet.'}</Alert>;
  }

  return (
    <Alert tone="success">
      Testkortet er sendt til Teams-kanalen
      {result.mentions.length > 0 ? ` og nævner ${result.mentions.join(', ')}` : ''}.
      {result.unknown_mentions.length > 0
        ? ` ${result.unknown_mentions.join(', ')} blev ikke nævnt — ingen bruger har den adresse.`
        : ''}
    </Alert>
  );
}

/**
 * Kortet, som Teams vil vise det.
 *
 * Omtalerne vises som chips frem for som `<at>Navn</at>`: taggen er den tekniske form, og den
 * siger ikke en læser noget om, hvad der sker. Chippen siger det.
 */
function TeamsMessagePreview({ preview }: { preview: ReturnType<typeof parseTeamsMessage> }) {
  if (preview.lines.length === 0) {
    return (
      <p className="text-xs text-ink-muted">
        Ingen besked. Reglen sender ikke noget til Teams-kanalen.
      </p>
    );
  }

  return (
    <div className="rounded-md border border-line bg-canvas p-3">
      <p className="mb-2 text-xs font-semibold text-ink-muted">Sådan ser kortet ud</p>

      <div className="space-y-1.5">
        {preview.lines.map((line, index) => (
          <p key={index} className="text-xs">
            {renderLine(line)}
          </p>
        ))}
      </div>

      {preview.mentions.length > 0 ? (
        <p className="mt-3 text-xs text-ink-muted">
          Nævner {preview.mentions.map((mention) => mention.name).join(', ')} — de får en
          notifikation i Teams.
        </p>
      ) : null}
    </div>
  );
}

/** `<at>Navn</at>` bliver til en chip; resten står som tekst. */
function renderLine(line: string) {
  return line.split(/(<at>[^<]*<\/at>)/g).map((part, index) => {
    const mention = /^<at>([^<]*)<\/at>$/.exec(part);
    if (!mention) return <span key={index}>{part}</span>;

    return (
      <span key={index} className="rounded bg-brand-50 px-1 font-medium text-brand-800">
        @{mention[1]}
      </span>
    );
  });
}
